import type { JSX } from 'react';
import { useEffect } from 'react';
import {
  ActionIcon,
  AppShell,
  Badge,
  Burger,
  Group,
  Menu,
  NavLink,
  Stack,
  Text,
  Title,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ROLE_LABELS, type CurrentUser } from '@pst/shared';
import { api, offersApi } from '../api';
import { ChangePasswordModal } from './ChangePasswordModal';
import { ProfileModal } from './ProfileModal';
import { ErrorBoundary } from '../ui/ErrorBoundary';
import { UndoButton } from '../ui/undo';
import { FallbackContextMenu } from '../ui/FallbackContextMenu';
import { Mark } from '../ui/Mark';
import { ExportMenu } from '../export/ExportMenu';
import { DownloadsProvider } from '../export/downloads';
import { LIBRARY_VIEWS } from './moduleViews';
import { linkTo } from '../ui/links';
import { NavQuick } from './NavQuick';
import { idInPath, noteRecent } from './navRecent';

/**
 * Application frame.
 *
 * The main navigation is deliberately short. PST is project-centred: the
 * areas of a project (schedule now, costs and the rest later) are tabs INSIDE
 * the project, not entries here. What remains here are the things that are not
 * about one project - and short lists of the projects and offers opened last,
 * so switching between them takes one click. The sidebar folds away entirely, because a schedule wants
 * every pixel of width it can get.
 */
export function Shell({ user }: { user: CurrentUser }): JSX.Element {
  const [mobileOpen, mobile] = useDisclosure();
  // Open by default on a desktop - the sidebar is where the cross-project views are; the choice is kept.
  const [desktopOpen, desktop] = useDisclosure(storedSidebar(), { onOpen: () => keepSidebar(true), onClose: () => keepSidebar(false) });
  const [passwordOpen, password] = useDisclosure();
  const [profileOpen, profile] = useDisclosure();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  const health = useQuery({ queryKey: ['health'], queryFn: api.health, staleTime: 60_000 });
  const instance = health.data?.instance;
  // A second installation, e.g. the presentation copy, says so in the browser tab too.
  useEffect(() => {
    if (instance) document.title = `PST · ${instance}`;
  }, [instance]);
  const logout = useMutation({ mutationFn: api.logout, onSuccess: () => queryClient.clear() });
  const logoutEverywhere = useMutation({
    mutationFn: api.logoutEverywhere,
    onSuccess: () => queryClient.clear(),
  });

  const link = (path: string) => linkTo(navigate, path, mobile.close);
  const canManageUsers = user.permissions.includes('users.manage');
  const can = (permission: string): boolean => user.permissions.includes(permission);
  const projects = useQuery({ queryKey: ['projects'], queryFn: api.projects });
  const offers = useQuery({ queryKey: ['offers'], queryFn: offersApi.list, enabled: can('offers.read') });
  const currentProject = idInPath('projects', location.pathname);
  const currentOffer = idInPath('offers', location.pathname);
  useEffect(() => {
    if (currentProject) noteRecent('projects', currentProject);
    if (currentOffer) noteRecent('offers', currentOffer);
  }, [currentProject, currentOffer]);
  const projectItems = (projects.data ?? []).map((p) => ({ id: p.id, code: p.number, name: p.name, archived: p.archivedAt !== null }));
  // Offers still to work on fill the list; the newest first.
  const offerItems = [...(offers.data ?? [])].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .map((o) => ({ id: o.id, code: o.code, name: o.name, open: o.status === 'entwurf' || o.status === 'abgegeben' }));
  const hasLibraries = LIBRARY_VIEWS.some((v) => can(v.permission));

  return (
    <DownloadsProvider>
    <AppShell
      header={{ height: 48 }}
      navbar={{
        width: 230,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpen, desktop: !desktopOpen },
      }}
      padding="sm"
      styles={{
        main: { display: 'flex', flexDirection: 'column', height: '100dvh', minHeight: 0 },
      }}
    >
      <AppShell.Header>
        <Group h="100%" px="sm" justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <Burger opened={mobileOpen} onClick={mobile.toggle} hiddenFrom="sm" size="sm" aria-label="Menü" />
            <Tooltip label={desktopOpen ? 'Seitenleiste ausblenden' : 'Seitenleiste einblenden'}>
              <ActionIcon variant="subtle" color="gray" onClick={desktop.toggle} visibleFrom="sm" aria-label="Seitenleiste ein- oder ausblenden">
                {desktopOpen ? '◀' : '☰'}
              </ActionIcon>
            </Tooltip>
            <Group gap={8} wrap="nowrap" style={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none' }} component="a" {...link('/')} aria-label="PST – zur Projektliste">
              <Mark size={24} />
              <Title order={4} style={{ letterSpacing: '0.08em', fontWeight: 650 }}>PST</Title>
              <Text size="xs" c="dimmed" visibleFrom="sm" style={{ letterSpacing: '0.02em' }}>Projektsteuerung</Text>
            </Group>
            {instance && <Badge color="orange" variant="filled" title="Eine eigene Installation mit eigener Datenbank, nicht die echten Daten">{instance}</Badge>}
          </Group>

          <Group gap={4} wrap="nowrap">
            <UndoButton />
            <ExportMenu />
            <FallbackContextMenu />
            <Tooltip label={colorScheme === 'dark' ? 'Helle Ansicht' : 'Dunkle Ansicht'}>
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => setColorScheme(colorScheme === 'dark' ? 'light' : 'dark')}
                aria-label="Helle oder dunkle Ansicht"
              >
                {colorScheme === 'dark' ? '☀' : '☽'}
              </ActionIcon>
            </Tooltip>
            <Menu position="bottom-end" width={240}>
              <Menu.Target>
                <Group gap={6} style={{ cursor: 'pointer' }} wrap="nowrap" role="button" aria-label="Menü des eigenen Kontos" tabIndex={0}>
                  <Text size="sm" visibleFrom="xs">{user.callName}</Text>
                  <Badge size="xs" variant="light">{ROLE_LABELS[user.role]}</Badge>
                </Group>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{user.displayName} · {user.email}</Menu.Label>
                <Menu.Item onClick={profile.open}>Profil</Menu.Item>
                <Menu.Item onClick={password.open}>Passwort ändern</Menu.Item>
                <Menu.Item component="a" href="/api/docs/PST-Handbuch.pdf" target="_blank" rel="noopener">Handbuch (PDF)</Menu.Item>
                {canManageUsers && <Menu.Item component="a" {...link('/benutzer')}>Nutzende verwalten</Menu.Item>}
                <Menu.Item onClick={() => logout.mutate()} disabled={logout.isPending}>Abmelden</Menu.Item>
                <Menu.Item
                  color="critical"
                  onClick={() => logoutEverywhere.mutate()}
                  disabled={logoutEverywhere.isPending}
                >
                  Auf allen Geräten abmelden
                </Menu.Item>
                <Menu.Divider />
                <Menu.Label>
                  System {health.data?.status === 'ok' ? 'in Ordnung' : health.data ? 'gestört' : '…'}
                  {health.data?.version ? ` · Version ${health.data.version}` : ''}
                </Menu.Label>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs" style={{ overflowY: 'auto' }}>
        {/* The work in the order it happens: first the offer, then the project (AP-89). */}
        {can('offers.read') && (
          <NavQuick
            kind="offers" label="Angebote" description="kalkulieren, Varianten, Unterlagen" listPath="/angebote"
            onList={location.pathname.startsWith('/angebote') && !currentOffer}
            items={offerItems} candidate={(o) => o.open} currentId={currentOffer}
            total={offerItems.length} pathname={location.pathname} link={link}
          />
        )}
        <NavQuick
          kind="projects" label="Projekte" description="einzeln öffnen und bearbeiten" listPath="/"
          onList={location.pathname === '/'}
          items={projectItems} candidate={(p) => !p.archived} currentId={currentProject}
          total={projectItems.length} pathname={location.pathname} link={link}
        />

        {/* Everything below spans every project one may see; each entry shows only when its module is active. */}
        <NavGroup title="Über alle Projekte" hint="Daten und Vergleiche aus allen Projekten, die Sie sehen dürfen" />
        {can('scheduling.read') && <NavLink label="Aufgaben" description="To-do aller Terminpläne" active={location.pathname === '/aufgaben'} component="a" {...link('/aufgaben')} />}
        {can('scheduling.read') && <NavLink label="Kalender" description="alle Termine, Abo-Link" active={location.pathname === '/kalender'} component="a" {...link('/kalender')} />}
        {can('projectdata.read') && <NavLink label="Zeiten" description="Kontingente und Buchungen je Projekt" active={location.pathname === '/zeiten'} component="a" {...link('/zeiten')} />}
        {can('costs.read') && <NavLink label="Kostenvergleich" description="Stufen und Kontrolle je Projekt" active={location.pathname === '/kosten'} component="a" {...link('/kosten')} />}
        {can('scheduling.read') && <NavLink label="Termin- und Dauervergleich" description="Plan, Ziel, Basisplan je Projekt" active={location.pathname === '/termine'} component="a" {...link('/termine')} />}
        <NavLink label="Mappe" description="Blätter aus mehreren Projekten als PDF/Excel" active={location.pathname === '/mappe'} component="a" {...link('/mappe')} />

        {/* The administration sits at the foot of the sidebar, out of the way of the daily work. */}
        <div style={{ flex: 1 }} />
        {(canManageUsers || hasLibraries) && <NavGroup title="Verwaltung" />}
        {canManageUsers && <NavLink label="Einstellungen" description="Verbindungen, Abzüge, KI, Vorlagen, Darstellung, Beispiel, Protokoll" active={location.pathname.startsWith('/einstellungen')} component="a" {...link('/einstellungen')} />}
        {canManageUsers && <NavLink label="Nutzende" description="Konten, Rollen, Einladungen" active={location.pathname === '/benutzer'} component="a" {...link('/benutzer')} />}
        {hasLibraries && <NavLink label="Bibliotheken" description="Kennwerte, DIN 276 und 277, Ausschreibungstexte" active={location.pathname.startsWith('/bibliotheken')} component="a" {...link('/bibliotheken')} />}
      </AppShell.Navbar>

      <AppShell.Main>
        <ErrorBoundary resetKey={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </AppShell.Main>

      <ChangePasswordModal opened={passwordOpen} onClose={password.close} />
      <ProfileModal user={user} opened={profileOpen} onClose={profile.close} />
    </AppShell>
    </DownloadsProvider>
  );
}

const SIDEBAR_KEY = 'pst.sidebar.open';
function storedSidebar(): boolean {
  try { return window.localStorage.getItem(SIDEBAR_KEY) !== '0'; } catch { return true; }
}
function keepSidebar(open: boolean): void {
  try { window.localStorage.setItem(SIDEBAR_KEY, open ? '1' : '0'); } catch { /* private window */ }
}

/** A heading between groups of the sidebar. */
function NavGroup({ title, hint }: { title: string; hint?: string }): JSX.Element {
  return (
    <Stack gap={0} px="sm" pt="md" pb={4} style={{ borderTop: '1px solid var(--mantine-color-default-border)', marginTop: 8 }}>
      <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.05em' }}>{title}</Text>
      {hint && <Text size="xs" c="dimmed">{hint}</Text>}
    </Stack>
  );
}

